import { useState, useMemo } from 'react'
import { useGet } from 'react-json-api-client'

interface DataProviderType<R> {
    completed: boolean
    currentPage?: number
    data?: R
    handlePageSizeChange: (n: number) => void
    handleChangeCurrentPage: (n: number) => void
    handleNextPage: () => number
    handlePreviousPage: () => number
    loading?: boolean
    total?: number
    pages?: number
    pageSize?: number
}

type ConfigurationType<R> = {
    dataProcessor?: (data: R) => R
    recordsPerPage?: number
    url?: string
    sorts?: { [key: string]: number | undefined }
}

type GetResponse<R> = {
    data: R[]
    loading: boolean
    total?: number
}

type PageInfoType = {
    'current-page': number
    from: number
    'per-page': number
    to: number
    total: number
    'last-page': number
}

const useSorterResolver = (sorts?: { [key: string]: number | undefined }) => {
    return Object.keys(sorts || {}).reduce((result, sortKey) => {
        if (sorts) {
            const currentValue = sorts[sortKey]
            if (currentValue === 1) {
                result.push(sortKey)
            } else if (currentValue === -1) {
                result.push(`-${sortKey}`)
            }
        }
        return result
    }, [] as string[])
}

/**
 * This hook allows to fetch and preprpcess the data fetched by the grid
 * @author Alejandro Quiroz <alejandro.devop@gmail.com>
 * @version 1.0.0
 * @param options
 * @returns
 */
const useDataProvider = <R extends Object>(
    options: ConfigurationType<R> = {}
): DataProviderType<R> => {
    const { recordsPerPage: defaultRecodsPerPage, dataProcessor, url, sorts } = options
    const [pageSizeControl, setPageSize] = useState<number>(defaultRecodsPerPage || 20)
    const [currentPageControl, setCurrentPage] = useState<number>(1)
    const [completed, setCompleted] = useState<boolean>(false)
    const sortsToApply = useSorterResolver(sorts)
    const { data, loading, meta } = useGet<GetResponse<R>>(url || '', {
        pageSize: pageSizeControl,
        currentPage: currentPageControl,
        sorts: sortsToApply,
        onCompleted: ({ meta }: { meta: { page: PageInfoType } }) => {
            setPageSize(meta.page['per-page'] || 20)
            setCompleted(true)
        }
    })
    const pageInfo = meta && meta.page ? meta.page : ({} as PageInfoType)
    const { total = 0 } = pageInfo
    const currentPage = pageInfo['current-page']
        ? parseInt(pageInfo['current-page'].toString(), 10)
        : 1
    const pageSize = pageInfo['per-page'] || 20
    const pages = total ? Math.ceil(total / pageSize) : 0
    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize)
        setCurrentPage(1)
    }

    const handleChangeCurrentPage = (newCurrentPage: number) => setCurrentPage(newCurrentPage)

    const handleNextPage = (): number => {
        let nextPage = currentPage + 1
        if (nextPage > pages) {
            nextPage = 1
        }
        setCurrentPage(nextPage)
        return nextPage
    }

    const handlePreviousPage = (): number => {
        let nextPage = currentPage - 1
        if (nextPage <= 0) {
            nextPage = pages
        }
        setCurrentPage(nextPage)
        return nextPage
    }
    const responseData = useMemo(() => {
        if (dataProcessor && typeof dataProcessor === 'function') {
            return dataProcessor(data as any)
        }
        return data
    }, [data, dataProcessor])
    return {
        completed,
        currentPage: currentPage,
        data: responseData as R,
        loading,
        handlePageSizeChange,
        handleChangeCurrentPage,
        handleNextPage,
        handlePreviousPage,
        total,
        pages,
        pageSize
    }
}

export default useDataProvider
